import { useState } from 'react';

type Step1Answer = '9810' | '0110' | '1039' | 'a-class' | '';
type Step2Answer = 'new' | 'stable' | 'growing' | '';

type DetailAnswers = {
  entities: string;
  revenue: string;
  taxRefund: string;
  duty: string;
  accounting: string;
};

export default function Recommendation() {
  const [step, setStep] = useState<1 | 2>(1);
  const [businessType, setBusinessType] = useState<Step1Answer>('');
  const [userStatus, setUserStatus] = useState<Step2Answer>('');
  const [details, setDetails] = useState<DetailAnswers>({
    entities: '',
    revenue: '',
    taxRefund: '',
    duty: '',
    accounting: ''
  });

  const [recommendation, setRecommendation] = useState<{
    version: string;
    price: string;
    reasons: string[];
    feeStructure: {
      subscription: string;
      exemption: string;
      optional: string[];
    };
    warning?: string;
  } | null>(null);

  // Step 1 Questions
  const businessTypes = [
    {
      value: '9810',
      title: '9810 跨境电商（海外仓/平台）',
      subtitle: '计费基数=年平台销售额'
    },
    {
      value: '0110',
      title: '0110 一般贸易出口',
      subtitle: '计费基数=年报关金额'
    },
    {
      value: '1039',
      title: '1039 无票出口',
      subtitle: '仅年度套餐'
    },
    {
      value: 'a-class',
      title: '我只想先跑通一票/买按次工具',
      subtitle: '走A类按次计费'
    }
  ];

  const userStatuses = [
    { value: 'new', label: '新手/偶尔做', subtitle: '想先跑通流程' },
    { value: 'stable', label: '稳定做，每月持续出货', subtitle: '需要系统化留痕' },
    { value: 'growing', label: '规模增长/合规压力大', subtitle: '要差异留痕+值守/服务包' }
  ];

  // Step 2 Questions (dynamic based on businessType)
  const getRevenueLabel = () => {
    if (businessType === '9810') return '年平台销售额';
    if (businessType === '0110' || businessType === '1039') return '年报关金额';
    return '年度规模';
  };

  const detailQuestions = [
    {
      key: 'entities' as keyof DetailAnswers,
      question: '主体数（企业户数）',
      options: ['1户', '2户', '3-5户', '5户以上']
    },
    {
      key: 'revenue' as keyof DetailAnswers,
      question: getRevenueLabel(),
      options: ['≤300万', '300-500万', '500-1000万', '1000万以上']
    },
    {
      key: 'taxRefund' as keyof DetailAnswers,
      question: '是否需要退税？',
      options: ['不需要', '可能需要（想评估）', '确定需要']
    },
    {
      key: 'duty' as keyof DetailAnswers,
      question: '是否需要夜晚/周末值守？',
      options: ['不需要', '偶尔', '经常']
    },
    {
      key: 'accounting' as keyof DetailAnswers,
      question: '是否需要"核算服务包"（把账算清）',
      options: ['不需要', '需要']
    }
  ];

  const handleBusinessTypeSelect = (value: Step1Answer) => {
    setBusinessType(value);
    setUserStatus('');
    setDetails({
      entities: '',
      revenue: '',
      taxRefund: '',
      duty: '',
      accounting: ''
    });
    setRecommendation(null);
  };

  const handleUserStatusSelect = (value: Step2Answer) => {
    setUserStatus(value);
    
    // If A-class, recommend immediately
    if (businessType === 'a-class') {
      recommendAClass(value);
    } else {
      setStep(2);
    }
  };

  const handleDetailSelect = (key: keyof DetailAnswers, value: string) => {
    const newDetails = { ...details, [key]: value };
    setDetails(newDetails);

    // Auto calculate when all required fields are filled
    if (newDetails.entities && newDetails.revenue) {
      calculateRecommendation(newDetails);
    }
  };

  const recommendAClass = (status: Step2Answer) => {
    if (status === 'new') {
      setRecommendation({
        version: 'A类按次工具',
        price: '9.9元起',
        reasons: [
          '新手友好：先用低成本跑通一票流程',
          'A1 9.9元（订仓单×1+草单×1，不含申报提交）',
          'A2 99元（订仓单×1+草单×1+申报提交）'
        ],
        feeStructure: {
          subscription: '按次计费',
          exemption: '限购1次/户，30天内使用',
          optional: []
        },
        warning: '零人工介入；校验不通过退回补齐后重提；不承诺放行结果；单证默认1个月可下载留存'
      });
    } else {
      setRecommendation({
        version: 'A类全套餐次数包',
        price: '按次计费',
        reasons: [
          '稳定出货：全套餐次数包（含申报提交+回执回传）',
          'AI归类工具（按SPU计费）',
          '适合持续但不高频的业务'
        ],
        feeStructure: {
          subscription: '按次数包计费',
          exemption: '含申报回执回传',
          optional: ['不含专家审阅', '不含值守服务']
        },
        warning: '零人工介入；校验不通过退回补齐后重提；不承诺放行结果'
      });
    }
  };

  const calculateRecommendation = (ans: DetailAnswers) => {
    // 9810 Logic
    if (businessType === '9810') {
      // Check if basic version is suitable
      if ((ans.revenue === '≤300万') && (ans.entities === '1户' || ans.entities === '2户')) {
        // Check if tax refund needed
        if (ans.taxRefund === '确定需要') {
          setRecommendation({
            version: '9810 标准版',
            price: '9,000元/年起',
            reasons: [
              '你需要退税服务包，但基础版不支持加购退税包',
              '标准版可加购退税服务包，且支持规模扩展'
            ],
            feeStructure: {
              subscription: '9,000元/年起',
              exemption: '按年平台销售额超额分段累计（仅超额部分计费）',
              optional: ['退税服务包（可加购）', '核算服务包（可加购）', '值守包（1个月1次）']
            },
            warning: '基础版不支持退税包，需升级到标准/尊享版'
          });
        } else {
          setRecommendation({
            version: '9810 基础版',
            price: '4,000元/年起',
            reasons: [
              '适合小体量卖家（≤2户、≤300万）',
              '满足基本合规需求，性价比高'
            ],
            feeStructure: {
              subscription: '4,000元/年起',
              exemption: '不支持超额累进（超过上限必须升级）',
              optional: ['值守服务（可另购）']
            },
            warning: '基础版不支持超额累进，超过上限必须升级标准/尊享版'
          });
        }
      } else if (ans.entities === '3-5户' || ans.revenue === '500-1000万' || ans.revenue === '300-500万') {
        setRecommendation({
          version: '9810 标准版',
          price: '9,000元/年起',
          reasons: [
            `你的主体=${ans.entities}，基础版不覆盖；标准版可覆盖并支持规模扩展`,
            '服务期内可保留、可下载单证（基础版仅1个月）',
            ans.taxRefund === '确定需要' ? '你勾选了退税需求：标准版可加购退税服务包' : ''
          ].filter(Boolean),
          feeStructure: {
            subscription: '9,000元/年起',
            exemption: '按年平台销售额超额分段累计（仅超额部分计费）',
            optional: ['退税服务包（可加购）', '核算服务包（可加购）', '值守包（1个月1次）']
          }
        });
      } else {
        setRecommendation({
          version: '9810 尊享版',
          price: '12,000元/年起',
          reasons: [
            '大体量业务（>5户或>1000万），需要不限主体数',
            ans.duty === '经常' ? '你需要高频值守：尊享版提供1个月4次值守' : '尊享版提供更完善的服务支持',
            '服务期内可保留、可下载单证，优先技术支持'
          ],
          feeStructure: {
            subscription: '12,000元/年起',
            exemption: '按年平台销售额超额分段累计（仅超额部分计费）',
            optional: ['退税服务包（可加购）', '核算服务包（可加购）', '值守包（1个月4次）']
          }
        });
      }
    }

    // 0110 Logic
    if (businessType === '0110') {
      if (ans.revenue === '≤300万' && ans.entities === '1户') {
        setRecommendation({
          version: '0110 基础版',
          price: '8,000元/年起',
          reasons: [
            '适合小体量一般贸易（1户、≤300万）',
            '满足基本合规需求'
          ],
          feeStructure: {
            subscription: '8,000元/年起',
            exemption: '按年报关金额超额分段累计',
            optional: ['退税服务包（标准/高阶可选）', '核算服务包（可加购）']
          }
        });
      } else if (ans.entities === '2户' || ans.revenue === '300-500万') {
        setRecommendation({
          version: '0110 标准版',
          price: '12,000元/年起',
          reasons: [
            '适合≤2户的一般贸易业务',
            '包含值守服务，单证服务期内可保留可下载'
          ],
          feeStructure: {
            subscription: '12,000元/年起',
            exemption: '按年报关金额超额分段累计',
            optional: ['退税服务包（标准/高阶）', '核算服务包（可加购）', '值守包']
          }
        });
      } else {
        setRecommendation({
          version: '0110 尊享版',
          price: '16,000元/年起',
          reasons: [
            '适合≤5户或高频值守需求',
            '完善的服务支持和优先技术响应'
          ],
          feeStructure: {
            subscription: '16,000元/年起',
            exemption: '按年报关金额超额分段累计',
            optional: ['退税服务包（高阶）', '核算服务包（可加购）', '高频值守包']
          }
        });
      }
    }

    // 1039 Logic
    if (businessType === '1039') {
      setRecommendation({
        version: '1039 年度套餐（标准版）',
        price: '22,800元/年起',
        reasons: [
          '1039无票出口专属年度套餐',
          '超过区间按超额费率分段累计'
        ],
        feeStructure: {
          subscription: '22,800元/年起',
          exemption: '按年报关金额超额分段累计（仅超额部分计费）',
          optional: ['核算服务包（可加购）', '值守包']
        }
      });
    }
  };

  const resetRecommendation = () => {
    setStep(1);
    setBusinessType('');
    setUserStatus('');
    setDetails({
      entities: '',
      revenue: '',
      taxRefund: '',
      duty: '',
      accounting: ''
    });
    setRecommendation(null);
  };

  const scrollToPricing = () => {
    const element = document.getElementById('pricing');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const openConsultation = () => {
    const drawer = document.getElementById('consultation-drawer');
    if (drawer) drawer.classList.remove('translate-x-full');
  };

  const openAClassSection = () => {
    const element = document.getElementById('a-class-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section className="py-32 bg-white">
      <div className="max-w-[1000px] mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-800 mb-3">
            30秒找到适合你的版本
          </h2>
          <p className="text-gray-600">
            回答几个问题，系统自动推荐最优方案
          </p>
        </div>

        {/* Step 1: Business Type */}
        {step === 1 && (
          <div className="space-y-8">
            <div className="bg-gray-50 rounded-2xl p-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-6">
                Step 1：你现在要解决什么？
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                {businessTypes.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => handleBusinessTypeSelect(type.value as Step1Answer)}
                    className={`p-6 rounded-xl text-left transition-all cursor-pointer ${
                      businessType === type.value
                        ? 'bg-[#14B8A6] text-white shadow-lg scale-105'
                        : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-[#14B8A6] hover:scale-105'
                    }`}
                  >
                    <div className="font-semibold mb-1">{type.title}</div>
                    <div className={`text-sm ${businessType === type.value ? 'text-white/80' : 'text-gray-500'}`}>
                      {type.subtitle}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {businessType && businessType !== 'a-class' && (
              <div className="bg-gray-50 rounded-2xl p-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-6">
                  Step 2：你更像哪种状态？
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  {userStatuses.map((status) => (
                    <button
                      key={status.value}
                      onClick={() => handleUserStatusSelect(status.value as Step2Answer)}
                      className={`p-6 rounded-xl text-center transition-all cursor-pointer ${
                        userStatus === status.value
                          ? 'bg-[#14B8A6] text-white shadow-lg scale-105'
                          : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-[#14B8A6] hover:scale-105'
                      }`}
                    >
                      <div className="font-semibold mb-1">{status.label}</div>
                      <div className={`text-sm ${userStatus === status.value ? 'text-white/80' : 'text-gray-500'}`}>
                        {status.subtitle}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {businessType === 'a-class' && (
              <div className="bg-gray-50 rounded-2xl p-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-6">
                  你更像哪种状态？
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  {userStatuses.map((status) => (
                    <button
                      key={status.value}
                      onClick={() => handleUserStatusSelect(status.value as Step2Answer)}
                      className={`p-6 rounded-xl text-center transition-all cursor-pointer ${
                        userStatus === status.value
                          ? 'bg-[#14B8A6] text-white shadow-lg scale-105'
                          : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-[#14B8A6] hover:scale-105'
                      }`}
                    >
                      <div className="font-semibold mb-1">{status.label}</div>
                      <div className={`text-sm ${userStatus === status.value ? 'text-white/80' : 'text-gray-500'}`}>
                        {status.subtitle}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Detail Questions */}
        {step === 2 && businessType !== 'a-class' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-800">
                规模与服务偏好
              </h3>
              <button
                onClick={resetRecommendation}
                className="text-sm text-[#14B8A6] hover:underline cursor-pointer"
              >
                <i className="ri-arrow-left-line mr-1"></i>
                重新选择
              </button>
            </div>

            {detailQuestions.map((q, qIndex) => (
              <div key={qIndex} className="bg-gray-50 rounded-2xl p-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  {q.question}
                  {qIndex < 2 && <span className="text-red-500 ml-1">*</span>}
                </h3>
                <div className="flex flex-wrap gap-4">
                  {q.options.map((option, oIndex) => (
                    <button
                      key={oIndex}
                      onClick={() => handleDetailSelect(q.key, option)}
                      className={`px-6 py-3 rounded-full text-sm font-medium transition-all cursor-pointer ${
                        details[q.key] === option
                          ? 'bg-[#14B8A6] text-white shadow-lg scale-105'
                          : 'bg-white text-gray-700 border border-gray-200 hover:border-[#14B8A6] hover:scale-105'
                      }`}
                    >
                      {details[q.key] === option && (
                        <i className="ri-check-line mr-1"></i>
                      )}
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Recommendation Result */}
        {recommendation && (
          <div className="mt-12 bg-white rounded-3xl shadow-2xl p-12 animate-fadeIn">
            <div className="mb-8">
              <div className="inline-block px-4 py-1 bg-orange-100 text-orange-600 text-xs font-medium rounded-full mb-4">
                为您推荐
              </div>
              <h3 className="text-3xl font-bold text-gray-800 mb-3">
                {recommendation.version}
              </h3>
              <div className="flex items-baseline gap-2 mb-6">
                <span className="text-5xl font-bold text-[#FF6B35]">
                  {recommendation.price}
                </span>
              </div>

              {/* Reasons */}
              <div className="space-y-2 mb-6">
                {recommendation.reasons.map((reason, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <i className="ri-check-line text-green-500 text-lg mt-0.5 flex-shrink-0"></i>
                    <span className="text-gray-700">{reason}</span>
                  </div>
                ))}
              </div>

              {/* Warning */}
              {recommendation.warning && (
                <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <i className="ri-alert-line text-orange-600 text-lg mt-0.5 flex-shrink-0"></i>
                    <span className="text-sm text-orange-800">{recommendation.warning}</span>
                  </div>
                </div>
              )}

              {/* Fee Structure */}
              <div className="bg-gray-50 rounded-xl p-6 space-y-3">
                <h4 className="font-semibold text-gray-800 mb-3">费用结构</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-start gap-2">
                    <span className="text-gray-600 min-w-[80px]">订阅费：</span>
                    <span className="text-gray-800 font-medium">{recommendation.feeStructure.subscription}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-gray-600 min-w-[80px]">免税服务：</span>
                    <span className="text-gray-800">{recommendation.feeStructure.exemption}</span>
                  </div>
                  {recommendation.feeStructure.optional.length > 0 && (
                    <div className="flex items-start gap-2">
                      <span className="text-gray-600 min-w-[80px]">可选加购：</span>
                      <div className="flex-1">
                        {recommendation.feeStructure.optional.map((item, index) => (
                          <div key={index} className="text-gray-800">• {item}</div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="space-y-3">
              <button
                onClick={businessType === 'a-class' ? openAClassSection : scrollToPricing}
                className="w-full h-14 bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] text-white font-medium rounded-full hover:scale-105 transition-transform whitespace-nowrap cursor-pointer"
              >
                {businessType === 'a-class' ? '查看A类工具' : '立即开通'}
              </button>
              <button
                onClick={openConsultation}
                className="w-full h-14 bg-white border-2 border-[#14B8A6] text-[#14B8A6] font-medium rounded-full hover:bg-[#14B8A6] hover:text-white transition-all whitespace-nowrap cursor-pointer"
              >
                发我报价单
              </button>
              {businessType !== 'a-class' && (
                <button
                  onClick={openAClassSection}
                  className="w-full h-12 bg-gray-100 text-gray-700 text-sm font-medium rounded-full hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                >
                  先跑通一票（9.9/99元）
                </button>
              )}
              <button
                onClick={resetRecommendation}
                className="w-full h-10 text-gray-600 text-sm hover:text-gray-800 transition-colors cursor-pointer"
              >
                <i className="ri-refresh-line mr-1"></i>
                重新测算
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
